import type { UIMessage } from 'ai';
import { createScopedLogger } from '~/utils/logger';
import type { ChatHistoryItem } from './useChatHistory';
import type { StoreFileAction } from '~/types/actions';
import { isAssetFile } from '~/utils/fileIgnore';
import { setUniqueId, uniqueIdStore, userStore } from '~/lib/stores/user';
import { generateObfuscatedId } from '~/lib/utils/uniqueHash';
import type { ConnectionData, ProjectIdInterface } from '~/services/serviceInterface';
import { needsMigrationStore } from '~/lib/stores/projectIdMigration';
import * as Sentry from '@sentry/remix';

// In-memory flag to track if migration is in progress (prevents duplicate runs)
let migrationInProgress = false;

const logger = createScopedLogger('ChatHistory');

// Function to convert base64 to Blob
export function base64ToBlob(base64: string, mimeType: string = 'application/octet-stream'): Blob {
  // Remove data URL prefix if present
  const base64Data = base64.includes('base64,') ? base64.split('base64,')[1] : base64;

  // Convert base64 to binary
  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);

    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
}

// Process file actions to convert asset files from base64 to blobs
export function processFileActions(fileActions: StoreFileAction[]): StoreFileAction[] {
  return fileActions.map((action) => {
    // Only process create and update actions that have content property
    if ('content' in action && action.type === 'file') {
      if (isAssetFile(action.filePath) && typeof action.content === 'string') {
        // Determine MIME type from file extension
        const extension = action.filePath.split('.').pop()?.toLowerCase() || '';
        const mimeMap: Record<string, string> = {
          png: 'image/png',
          jpg: 'image/jpeg',
          jpeg: 'image/jpeg',
          gif: 'image/gif',
          ico: 'image/x-icon',
          webp: 'image/webp',
          bmp: 'image/bmp',
          tiff: 'image/tiff',
        };
        const mimeType = mimeMap[extension] || 'application/octet-stream';

        // Convert base64 to blob
        return {
          ...action,
          content: base64ToBlob(action.content, mimeType),
          isBlob: true,
        };
      }
    }

    return action;
  });
}

// Check if boltHistory database exists
export async function checkBoltHistoryExists(): Promise<boolean> {
  return new Promise((resolve) => {
    const request = indexedDB.open('boltHistory');

    request.onsuccess = () => {
      const db = request.result;

      try {
        if (db?.objectStoreNames?.length === 0) {
          console.log('[checkBoltHistoryExists] No object stores found in boltHistory database');

          /*
           * alert(
           *   "Old IndexDB existed but empty. You're now using the latest version of Dualite Alpha. No need to worry, a routine upgrade was done. \n\n Enjoy your day!",
           * );
           */
          db.close();
          resolve(false);
        }
      } catch (error) {
        /*
         * Sentry.captureException(error, {
         *   extra: {
         *     message: 'Error in db migration',
         *   },
         * });
         */
        console.log('db migration edge case failed. contact support');
      }

      console.log('[checkBoltHistoryExists] db:', db);
      db.close();
      resolve(true);
    };

    request.onerror = () => {
      resolve(false);
    };
  });
}

// Check if dualiteHistory database exists
export async function checkDualiteHistoryExists(): Promise<boolean> {
  return new Promise((resolve) => {
    const request = indexedDB.open('dualiteHistory');

    request.onsuccess = () => {
      const db = request.result;

      try {
        if (db?.objectStoreNames?.length === 0) {
          console.log('[checkDualiteHistoryExists] No object stores found in dualiteHistory database');
          db.close();
          resolve(false);
          return;
        }
      } catch (error) {
        console.log('[checkDualiteHistoryExists] Error checking dualiteHistory database:', error);
        db.close();
        resolve(false);
        return;
      }

      console.log('[checkDualiteHistoryExists] dualiteHistory database exists with stores');
      db.close();
      resolve(true);
    };

    request.onerror = () => {
      console.log('[checkDualiteHistoryExists] dualiteHistory database does not exist');
      resolve(false);
    };
  });
}

// Open boltHistory database
export async function openBoltDatabase(): Promise<IDBDatabase | undefined> {
  return new Promise((resolve) => {
    const request = indexedDB.open('boltHistory', 1);

    request.onsuccess = (event: Event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event: Event) => {
      resolve(undefined);
      logger.error((event.target as IDBOpenDBRequest).error);
    };
  });
}

// Delete boltHistory database after migration
export async function deleteBoltHistory(): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase('boltHistory');

    request.onsuccess = () => {
      logger.info('boltHistory database deleted successfully');
      resolve();
    };

    request.onerror = () => {
      logger.error('Failed to delete boltHistory database');
      reject(new Error('Failed to delete boltHistory database'));
    };
  });
}

// this is used at the top level and never rejects
export async function openDatabase(): Promise<IDBDatabase | undefined> {
  return new Promise((resolve) => {
    const request = indexedDB.open('dualiteHistory', 2);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const transaction = (event.target as IDBOpenDBRequest).transaction!;
      const oldVersion = event.oldVersion;

      // Version 1: Create object store and initial indices
      if (oldVersion < 1) {
        if (!db.objectStoreNames.contains('chats')) {
          const store = db.createObjectStore('chats', { keyPath: 'id' });
          store.createIndex('id', 'id', { unique: true });
          store.createIndex('urlId', 'urlId', { unique: true });
        }
      }

      // Version 2: Add projectId index and field to existing records
      if (oldVersion < 2) {
        const store = transaction.objectStore('chats');

        // Create the projectId index if it doesn't exist
        if (!store.indexNames.contains('projectId')) {
          store.createIndex('projectId', 'projectId', { unique: true });
          console.log('[projectId] Created projectId index');
        }

        /*
         * Schema updated. Data migration will be triggered when user token becomes available
         * The migration function will check database state to determine if migration is needed
         */
        console.log('[projectId] Schema updated (will migrate data when user token is available)');
      }
    };

    request.onsuccess = (event: Event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      resolve(db);
    };

    request.onerror = (event: Event) => {
      resolve(undefined);
      logger.error((event.target as IDBOpenDBRequest).error);
    };
  });
}

export async function runProjectIdMigration(db: IDBDatabase, ownerId: string): Promise<void> {
  // Prevent concurrent migration runs
  if (migrationInProgress) {
    console.log('[projectId] Migration already in progress, skipping');
    return;
  }

  // Check if migration is actually needed by querying the database
  const needsMigration = await checkIfMigrationNeeded(db);

  console.log('[projectId] needsMigration >>>', needsMigration);
  needsMigrationStore.set({
    needsMigration,
    migrationFailed: false,
    migrationError: undefined,
  });

  if (!needsMigration) {
    console.log('[projectId] Migration not needed (all records have projectId or no records exist)');
    return;
  }

  console.log('[projectId] Starting deferred migration with ownerId');
  migrationInProgress = true;

  try {
    await performProjectIdMigration(db, ownerId);
    needsMigrationStore.set({
      needsMigration: false,
      migrationFailed: false,
      migrationError: undefined,
    });
    console.log('[projectId] Migration completed successfully');
  } catch (error) {
    needsMigrationStore.set({
      needsMigration: true,
      migrationFailed: true,
      migrationError: error instanceof Error ? error.message : 'Failed to migrate your projects to cloud',
    });

    Sentry.captureException(error, {
      extra: {
        message: 'Uploading projects data to cloud failed',
        ownerId,
      },
    });
  } finally {
    migrationInProgress = false;
  }
}

// Helper function to check if migration is needed by examining database state
async function checkIfMigrationNeeded(db: IDBDatabase): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const getAllRequest = store.getAll();

    getAllRequest.onsuccess = () => {
      const projects = getAllRequest.result as ChatHistoryItem[];

      // If no projects exist, no migration needed
      if (projects.length === 0) {
        resolve(false);
        return;
      }

      // Check if any project is missing projectId
      const needsMigration = projects.some(
        (project) => !project.hasOwnProperty('projectId') || project.projectId === undefined,
      );

      resolve(needsMigration);
    };

    getAllRequest.onerror = () => reject(getAllRequest.error);
  });
}

async function performProjectIdMigration(db: IDBDatabase, ownerId: string): Promise<void> {
  console.log('[projectId] Starting migration with ownerId:', ownerId);

  // Read all existing projects
  const projects = await new Promise<ChatHistoryItem[]>((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const getAllRequest = store.getAll();

    getAllRequest.onsuccess = () => resolve(getAllRequest.result);
    getAllRequest.onerror = () => reject(getAllRequest.error);
  });

  const projectWithoutProjectId = projects.filter((project) => project.projectId === undefined);

  console.log(`[projectId] Found ${projectWithoutProjectId.length} projects to migrate`);

  // Prepare data for cloud upload
  const valuesArray = projectWithoutProjectId.map((project) => {
    const [framework, cssLib] = project.techStack?.framework.split('_') ?? [];
    const techStack = {
      framework,
      cssLib,
      language: project.techStack?.language,
      packageManager: project.techStack?.packageManager,
    };

    const connectionsData: ConnectionData[] = [];

    if (project.connections?.github) {
      connectionsData.push({
        connectionType: 'github',
        connectionData: project.connections?.github,
      });
    }

    if (project.connections?.supabase) {
      connectionsData.push({
        connectionType: 'supabase',
        connectionData: project.connections?.supabase,
      });
    }

    if (project.netlifyCreds) {
      connectionsData.push({
        connectionType: 'netlify',
        connectionData: {
          netlifyUrl: project.netlifyCreds.netlifyUrl,
          netlifySiteId: project.netlifyCreds.netlifySiteId,
          netlifyToken: project.netlifyCreds.netlifyToken,
          inspectDeployment: project.netlifyCreds.inspectDeployment,
        },
      });
    }

    return {
      ownerId,
      currentVersion: null,
      urlId: project.urlId,
      projectName: project.description,
      techStack,
      isGitRepo: project.isGitRepo,
      initSource: project.initSource,
      type: 'local',
      idbChatId: project.id,
      createdAt: project.timestamp,
      projectId: project.projectId,
      connectionsData,
      projectRules: project.projectRules,
    };
  });

  // Upload to cloud and get projectIds
  let projectIds: ProjectIdInterface[] = [];

  try {
    const response = await fetch('/api/projects', {
      method: 'POST',
      body: JSON.stringify({ projectData: valuesArray, type: 'upload' }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error();
    }

    const responseData = (await response.json()) as {
      message: ProjectIdInterface[];
    };

    projectIds = responseData.message;
    console.log('[projectId] Successfully uploaded projects to cloud');
  } catch (error) {
    throw new Error('Failed to migrate your projects to cloud');
  }

  console.log('[projectId] projectIds from the api before setting in idb >>>', projectIds);

  // Update local IndexedDB records with projectIds
  await new Promise<void>((resolve, reject) => {
    const updateTransaction = db.transaction(['chats'], 'readwrite');
    const updateStore = updateTransaction.objectStore('chats');

    projects.forEach((record) => {
      console.log('[projectId] record >>>', record.urlId);

      const newProjectId = projectIds.find(
        (p) =>
          (p.urlId === record.urlId && p.chatId === record.id) ||
          (record.urlId === undefined && p.chatId === record.id),
      )?.projectId;

      if (newProjectId) {
        record.projectId = newProjectId;

        console.log('[projectId] Updating record:', {
          id: record.id,
          urlId: record.urlId,
          projectId: newProjectId,
        });
      }

      updateStore.put(record);
    });

    updateTransaction.oncomplete = () => {
      console.log('[projectId] Completed adding projectId field to existing records');
      resolve();
    };

    updateTransaction.onerror = () => {
      console.error('[projectId] Error updating records:', updateTransaction.error);
      reject(updateTransaction.error);
    };
  });

  console.log('[projectId] Migration completed with setting projectIds in idb');
}

export async function setProjectId(db: IDBDatabase, chatId: string, projectId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const getRequest = store.get(chatId);

    console.log('[projectId] projectId and chatId from setProjectId >>>', projectId, chatId);

    getRequest.onsuccess = () => {
      const existingChat = getRequest.result;

      if (existingChat) {
        console.log('[projectId] existingChat from setProjectId >>>', existingChat);

        existingChat.projectId = projectId;

        const putRequest = store.put(existingChat);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

export async function updatDataWithProjectId(db: IDBDatabase, newData: ChatHistoryItem[]): Promise<void> {
  console.log('[projectId] Updating data with projectId');

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');

    const requests = [];

    for (const data of newData) {
      const request = store.put(data);
      requests.push(request);
    }

    Promise.all(requests).then(() => resolve());
    Promise.all(requests).catch((error) => reject(error));
  });
}

export async function getAll(db: IDBDatabase): Promise<ChatHistoryItem[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result as ChatHistoryItem[]);
    request.onerror = () => reject(request.error);
  });
}
export async function getAlltoStoreInDB(db: IDBDatabase): Promise<ChatHistoryItem[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const index = store.index('urlId');
    const urlIds: ChatHistoryItem[] = [];

    const request = index.openCursor();

    request.onsuccess = (event: Event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

      if (cursor) {
        const value = cursor.value;

        if (value) {
          urlIds.push(value);
        }

        cursor.continue();
      } else {
        resolve(urlIds);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

// = { language: 'ts', framework: 'react' }

// Add these type definitions near the top of the file with other types
export interface RepoDetails {
  repoName: string;
  ownerName: string;
  branch: string;
}

export interface SupabaseData {
  projectId: string;
  projectName: string;
  anonKey: string;
  organizationId: string;
  organizationName: string;
}

// Update Connections type to allow both github and supabase
export type Connections = {
  github?: RepoDetails;
  supabase?: SupabaseData;
};

export async function setMessages(
  db: IDBDatabase,
  id: string,
  messages: UIMessage[],
  framework: string,
  language: string,
  packageManager: string,
  isGitRepo: boolean,
  initSource?: string,
  urlId?: string,
  description?: string,
  connections?: Connections,
): Promise<void> {
  const timestamp = new Date().toISOString();
  const userEmail = userStore.get().email;
  let uniqueId: string | undefined = undefined;

  if (userEmail && timestamp) {
    uniqueId = await generateObfuscatedId(userEmail, timestamp);
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const techStack = { framework, language, packageManager: packageManager || 'yarn' };
    console.log('the urlId', urlId);

    // First try to get existing chat
    const getRequest = store.get(id);

    getRequest.onsuccess = async () => {
      const existingChat = getRequest.result;
      const finalUniqueId = existingChat?.uniqueId || uniqueId;
      setUniqueId(finalUniqueId || '');

      console.log('existingChat from setMessages >>>', existingChat);

      // Prepare the chat data
      const chatData = {
        ...existingChat,
        messages,
        id,
        urlId,
        description: existingChat?.description || description,
        timestamp,
        techStack: existingChat?.techStack || techStack,
        isGitRepo: existingChat?.isGitRepo || isGitRepo || false,
        connections: existingChat?.connections || connections,
        initSource: existingChat?.initSource || initSource,
        uniqueId: finalUniqueId,
      };

      // Put the new or updated chat data
      const putRequest = store.put(chatData);

      putRequest.onsuccess = () => {
        //  console.log(`[setMessages] Success ${existingChat ? 'updating' : 'creating'} chat:`, chatData);
        resolve();
      };

      putRequest.onerror = (error) => {
        // console.error(`[setMessages] Error ${existingChat ? 'updating' : 'creating'} chat:`, error);
        reject(putRequest.error);
      };
    };

    getRequest.onerror = (error) => {
      // console.error('[setMessages] Error checking for existing chat:', error);
      reject(getRequest.error);
    };
  });
}

export async function setFileActions(
  db: IDBDatabase,
  id: string,
  fileActions: StoreFileAction[],
  initSource: string = 'alpha',
  isGitRepo: boolean = false,
  connections?: Connections,
): Promise<void> {
  // Prepare all async data BEFORE starting the transaction
  const timestamp = new Date().toISOString();

  const uniqueId = uniqueIdStore.get().uniqueId || '';

  const processedFileActions = processFileActions(fileActions);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');

    // First try to get existing chat
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existingChat = getRequest.result;
      const initSourceData = existingChat?.initSource || initSource;
      const isGitRepoData = existingChat?.isGitRepo || isGitRepo;
      const connectionsData = existingChat?.connections || connections;
      const finalUniqueId = existingChat?.uniqueId || uniqueId;
      console.log('[setFileActions] finalUniqueId: ', finalUniqueId);
      setUniqueId(finalUniqueId || '');

      // Use precomputed uniqueId
      const chatData = {
        ...existingChat,
        id,
        fileActions: processedFileActions,
        timestamp,
        initSource: initSourceData,
        isGitRepo: isGitRepoData,
        connections: connectionsData,
        uniqueId: finalUniqueId,
      };

      const putRequest = store.put(chatData);

      putRequest.onsuccess = () => resolve();
      putRequest.onerror = (error) => reject(putRequest.error);
    };

    getRequest.onerror = (error) => reject(getRequest.error);
  });
}

export async function getFileActions(db: IDBDatabase, id: string): Promise<StoreFileAction[]> {
  return new Promise((resolve, reject) => {
    if (!db || !id) {
      resolve([]);
      return;
    }

    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result.fileActions);
    request.onerror = () => reject(request.error);
  });
}

export async function setMigratedMessages(
  db: IDBDatabase,
  id: string,
  messages: UIMessage[],
  fileActions: StoreFileAction[],
  timestamp: string,
  urlId?: string,
  description?: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');

    console.log('[refresh] files from db: ', fileActions);

    // Process fileActions to convert asset files from base64 to blobs
    const processedFileActions = processFileActions(fileActions);

    const request = store.put({
      id,
      messages,
      urlId,
      description,
      fileActions: processedFileActions,
      timestamp,
    });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function setLatestMigratedMessages(db: IDBDatabase, messageData: ChatHistoryItem): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');

    const request = store.put(messageData);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getMessages(db: IDBDatabase, id: string): Promise<ChatHistoryItem> {
  const messages = (await getMessagesById(db, id)) || (await getMessagesByUrlId(db, id));
  console.log('messages >>>', messages);

  return messages;
}

export async function getMessagesByUrlId(db: IDBDatabase, id: string): Promise<ChatHistoryItem> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const index = store.index('urlId');
    const request = index.get(id);

    request.onsuccess = () => resolve(request.result as ChatHistoryItem);
    request.onerror = () => reject(request.error);
  });
}

export async function getMessagesById(db: IDBDatabase, id: string): Promise<ChatHistoryItem> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result as ChatHistoryItem);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteById(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.delete(id);

    request.onsuccess = () => resolve(undefined);
    request.onerror = () => reject(request.error);
  });
}

export async function getNextId(db: IDBDatabase): Promise<string> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.getAllKeys();

    request.onsuccess = () => {
      const highestId = request.result.reduce((cur, acc) => Math.max(+cur, +acc), 0);
      resolve(String(+highestId + 1));
    };

    request.onerror = () => reject(request.error);
  });
}

export async function getStoreSize(db: IDBDatabase): Promise<number> {
  return new Promise((resolve, reject) => {
    let size = 0;
    const transaction = db.transaction('chats');
    const objectStore = transaction.objectStore('chats');

    objectStore.openCursor().onsuccess = function (event) {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

      if (cursor) {
        const value = JSON.stringify(cursor.value);
        size += value.length;
        cursor.continue();
      } else {
        resolve(size); // Only resolve after all items are read
      }
    };

    objectStore.openCursor().onerror = function (event) {
      reject(event);
    };
  });
}

export async function getUrlId(db: IDBDatabase, id: string): Promise<string> {
  console.log('getUrlId called with id:', id);

  const idList = await getUrlIds(db);
  console.log('Retrieved idList:', idList);

  if (!idList.includes(id)) {
    console.log('ID not in idList, returning:', id);
    return id;
  } else {
    let i = 2;
    console.log('ID found in idList, starting increment loop');

    while (idList.includes(`${id}-${i}`)) {
      console.log(`ID ${id}-${i} already exists, incrementing i`);
      i++;
    }

    const newId = `${id}-${i}`;
    console.log('Returning new ID:', newId);
    return newId;
  }
}

async function getUrlIds(db: IDBDatabase): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const idList: string[] = [];

    const request = store.openCursor();

    request.onsuccess = (event: Event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      console.log('cursor >>>', cursor, cursor?.value);

      if (cursor) {
        idList.push(cursor.value.urlId);
        cursor.continue();
      } else {
        resolve(idList);
      }
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export const updateNetlifyUrl = async (
  db: IDBDatabase,
  chatId: string,
  deployData: {
    netlifyUrl: string;
    netlifySiteId: string;
    netlifyToken: string;
    inspectDeployment: string;
  },
) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        chat.netlifyCreds = deployData;

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(chat);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export const updateProjectRules = async (db: IDBDatabase, chatId: string, rules: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        // Update or set the projectRules field
        chat.projectRules = rules;

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(true);

        updateRequest.onerror = () => {
          console.error('[db] Error updating rules:', updateRequest.error);
          resolve(false);
        };
      } else {
        console.warn('[db] Chat not found when updating rules');
        resolve(false);
      }
    };

    request.onerror = () => {
      console.error('[db] Error getting chat for rules update:', request.error);
      resolve(false);
    };
  });
};

export const getRules = async (db: IDBDatabase, chatId: string): Promise<string | null> => {
  return new Promise((resolve) => {
    const transaction = db.transaction(['chats'], 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;
      resolve(chat?.projectRules || null);
    };

    request.onerror = () => {
      console.error('[db] Error loading rules:', request.error);
      resolve(null); // Resolve with null instead of rejecting
    };
  });
};

export async function getFramework(
  db: IDBDatabase,
  chatId: string,
): Promise<{ framework: string; language: string } | undefined> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      console.log('request >>>', request.result);

      const chat = request.result;
      resolve(chat?.techStack);
    };

    request.onerror = () => reject(request.error);
  });
}

export const updateFramework = async (db: IDBDatabase, chatId: string, framework: string, language: string) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        chat.techStack = { language, framework };

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(chat);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

// Add this interface near the top with other interfaces
export interface GitRepoStatus {
  isGitRepo: boolean;
}

// Add this function with other export functions
export async function getGitRepoStatus(chatId: string): Promise<GitRepoStatus> {
  console.log('[db] openDatabase called from getGitRepoStatus');

  const db = await openDatabase();

  if (!db) {
    throw new Error('Failed to open database');
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;
      db.close();
      resolve({ isGitRepo: chat?.isGitRepo ?? false });
    };

    request.onerror = () => {
      console.error('[db] Error loading git repo status:', request.error);
      db.close();
      reject(request.error);
    };
  });
}

// = { platform: RepoDetails }
export async function getGitRepo(chatId: string): Promise<Connections | null> {
  console.log('[db] openDatabase called from getGitRepo');

  const db = await openDatabase();

  if (!db) {
    throw new Error('Failed to open database');
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;
      console.log('[db] getGitRepo chat result:', chat);

      db.close();

      if (!chat) {
        console.log('[db] No chat found for ID:', chatId);
        resolve(null);
        return;
      }

      if (!chat.connections) {
        console.log('[db] No connections found for chat:', chatId);
        resolve(null);
        return;
      }

      resolve(chat.connections);
    };

    request.onerror = () => {
      console.error('[db] Error loading git repo:', request.error);
      db.close();
      reject(request.error);
    };
  });
}

export const updateGithubStatus = async (db: IDBDatabase, chatId: string, connections: Connections) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;
      console.log('connections 2 >>>', connections);

      if (chat) {
        if (connections && Object.keys(connections).length > 0) {
          chat.isGitRepo = true;
          chat.connections = { ...chat.connections, ...connections };
          console.log('chat.connections after update >>>', chat.connections);
        } else {
          chat.isGitRepo = false;
          chat.initSource = 'alpha';
        }

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(chat);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export const updatePackageManager = async (db: IDBDatabase, chatId: string, packageManager: string) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        chat.techStack.packageManager = packageManager;

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(chat);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export const clearGithubConnection = async (db: IDBDatabase, chatId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        // If connections exist, remove github while preserving other connections
        if (chat.connections) {
          const { github, ...restConnections } = chat.connections;
          chat.connections = restConnections;
          console.log('chat connections after removing github >>>', chat.connections);

          // If no more connections exist, set isGitRepo to false
          chat.isGitRepo = false;
        }

        console.log('chat >>>', chat.connections, chat.isGitRepo);

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export async function getUniqueId(chatId: string): Promise<string | undefined> {
  console.log('chatId from getUniqueId >>>', chatId);

  if (!chatId) {
    setUniqueId('');
    return '';
  }

  console.log('[db] openDatabase called from getUniqueId');

  const db = await openDatabase();

  if (!db) {
    throw new Error('Failed to open database');
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;
      db.close();

      if (!chat?.uniqueId) {
        setUniqueId('');
        resolve('');
      } else {
        setUniqueId(chat?.uniqueId);
        resolve(chat?.uniqueId);
      }
    };

    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}

export interface SupabaseData {
  projectId: string;
  projectName: string;
  anonKey: string;
  organizationId: string;
  organizationName: string;
}

export async function getSupabaseData(db: IDBDatabase, chatId: string): Promise<SupabaseData | null> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;
      console.log('getSupabaseData hitting >>>', chat.connections);

      resolve(chat?.connections?.supabase as SupabaseData | null);
      db.close();
    };

    request.onerror = () => reject(request.error);
  });
}

export async function updateSupabaseData(
  db: IDBDatabase,
  chatId: string,
  supabaseProjectId: string,
  supabaseProjectName: string,
  supabaseAnonKey: string,
  supabaseOrganizationId: string,
  supabaseOrganizationName: string,
) {
  console.log('updateSupabase func called >>>', chatId);

  if (
    supabaseProjectId &&
    supabaseProjectName &&
    supabaseAnonKey &&
    supabaseOrganizationId &&
    supabaseOrganizationName
  ) {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['chats'], 'readwrite');
      const store = transaction.objectStore('chats');
      const request = store.get(chatId);

      request.onsuccess = () => {
        const chat = request.result;
        console.log('chat >>>', chat.connections);

        chat.connections = {
          ...chat.connections,
          supabase: {
            projectId: supabaseProjectId,
            projectName: supabaseProjectName,
            anonKey: supabaseAnonKey,
            organizationId: supabaseOrganizationId,
            organizationName: supabaseOrganizationName,
          } as SupabaseData,
        };
        console.log('chat.connections after update >>>', chat.connections);

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(chat);
        updateRequest.onerror = () => reject(updateRequest.error);
      };

      request.onerror = () => reject(request.error);
    });
  } else {
    await clearSupabaseConnection(db, chatId);
    return Promise.resolve();
  }
}

export const clearSupabaseConnection = async (db: IDBDatabase, chatId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        // If connections exist, remove supabase while preserving other connections
        if (chat.connections) {
          const { supabase, ...restConnections } = chat.connections;
          chat.connections = restConnections;
          console.log('chat connections after removing supabase >>>', chat.connections);
        }

        console.log('chat >>>', chat.connections);

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};
